import React from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

// context
import { RMWContext } from 'app/shared/providers/RMWContext';

// utils
import { STATUS_KEY, getTagTypeByKey } from 'app/shared/utils/statusTagUtils';

import PropertiesActions from 'app/shared/actions/PropertiesActions';

// components
import PropertyPanel from 'app/shared/components/soh/PropertyPanel';
import AltTag from 'app/shared/components/AltTag';

// styled components
import {
    StyledAddressAndCTAContainer,
    StyledAddressContainer,
    StyledButton,
    StyledCard,
    StyledCityStateZipText,
    StyledPhotoAddressContainer,
    StyledPropertyPanelContainer,
    StyledPropertyPhotoContainer,
    StyledRamp,
    StyledStreetText,
} from './StyledComponents';

const PropertyCard = (props) => {
    const dispatch = useDispatch();

    // context
    const { paths } = React.useContext(RMWContext);
    const { listing } = props;

    const {
        active,
        isConnectedExperience,
        isFeed = '',
        isMultifamily,
        photoUrl: primaryPhotoUrl,
        status,
    } = listing;

    const { code, message: statusMessage } = status;

    const displayPanelWithSubTitle = isConnectedExperience && !isMultifamily;

    const getPropertyInfoLines = () => {
        const {
            address,
            name,
        } = listing;

        const {
            city,
            state,
            street,
            unit,
            zip,
        } = address;

        const mfSummary = isMultifamily ? name : `${city}, ${state} ${zip}`;
        const sfSummary = unit ? `${street} ${unit}` : street;

        const firstLine = isMultifamily ? mfSummary : sfSummary;
        const secondLine = isMultifamily ? sfSummary : mfSummary;

        return { firstLine, secondLine };
    };

    /**
     * Returns the cta path
     *
     * if connected experience - returns panel.urlPath
     * if not connected experience - return path to edit listing page
     */
    const getCTAPath = () => {
        const { aliasEncoded, panels } = listing;
        const panel = panels[0];

        const { propertiesPage } = paths;

        return displayPanelWithSubTitle
            ? panel.urlPath
            : `${propertiesPage}/${aliasEncoded}/listing`;
    };

    const ctaPath = getCTAPath();

    const handleCTAClick = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const { listingAlias: propertyId } = listing;

        // update on route change: fixes incorrect connected access
        // FIND: update listing isConnectedExperience
        dispatch(PropertiesActions.getIsConnectedExperience(propertyId)).finally(() => {
            window.router.transitionTo(ctaPath);
        });
    };

    const getCTAButton = () => {
        const activeTitle = 'Edit';
        const inactiveTitle = 'List';
        const feedTitle = 'Manage';
        let title = null;

        if (isFeed) {
            title = feedTitle;
        } else {
            title = active ? activeTitle : inactiveTitle;
        }

        return (
            <Link to={ctaPath}>
                <StyledButton
                    buttonType="secondary"
                    size="sm"
                    onClick={handleCTAClick}
                >
                    {title}
                </StyledButton>
            </Link>
        );
    };

    const getPanelList = () => {
        const {
            listingAlias,
            panels,
        } = listing;

        return panels.map((panelModel, index) => (
            <PropertyPanel
                key={index}
                isConnectedExperience={isConnectedExperience}
                listingAlias={listingAlias}
                panelModel={panelModel}
            />
        ));
    };

    const { firstLine, secondLine } = getPropertyInfoLines();

    return (
        <StyledCard className="SOHPropertyCard">
            <StyledPhotoAddressContainer onClick={handleCTAClick}>
                <StyledPropertyPhotoContainer
                    className="SOHPropertyCard-Property-Container"
                    primaryPhotoUrl={primaryPhotoUrl}
                >
                    <AltTag
                        appearance={getTagTypeByKey(STATUS_KEY.PROPERTY_DETAILS, code)}
                    >
                        {statusMessage}
                    </AltTag>

                    <StyledRamp>
                        <StyledAddressAndCTAContainer>
                            <StyledAddressContainer>
                                <StyledStreetText
                                    className="SOHPropertyCard-Address-Container-Street"
                                    fontType="h6"
                                    as="h5"
                                >
                                    {firstLine}
                                </StyledStreetText>

                                <StyledCityStateZipText
                                    className="SOHPropertyCard-Address-Container-CityStateZip"
                                    fontType="bodySmall"
                                    as="p"
                                >
                                    {secondLine}
                                </StyledCityStateZipText>
                            </StyledAddressContainer>

                            {!isConnectedExperience && getCTAButton()}
                        </StyledAddressAndCTAContainer>
                    </StyledRamp>

                </StyledPropertyPhotoContainer>
            </StyledPhotoAddressContainer>

            <StyledPropertyPanelContainer className="SOHPropertyCard-PropertyPanelContainer">
                {getPanelList()}
            </StyledPropertyPanelContainer>
        </StyledCard>
    );
};

PropertyCard.propTypes = {};
PropertyCard.defaultProps = {};

export { PropertyCard };
export default PropertyCard;
